import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import CircularProgress from '@mui/material/CircularProgress';

import { reset } from '../../api';
import { AuthForm, AuthFormButton, AuthFormInput, AuthFormLabel } from '../../features/auth';
import logo from '../../images/logo-login.png';
import { translateAuthError } from '../../lib/authError';
import LanguageControl from '../../shared/components/LanguageControl';

function Reset() {
    const { t, i18n } = useTranslation(undefined, { keyPrefix: 'Login' });

    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [stage, setStage] = useState('entry');

    const urlParams = new URLSearchParams(window.location.search);
    const email = urlParams.get('email');
    const token = urlParams.get('token');
    const urlLanguage = urlParams.get('language');

    useEffect(() => {
        if (!urlLanguage) {
            return;
        }
        const lower = urlLanguage.toLowerCase();
        if (['english', 'chinese', 'japanese'].includes(lower) && lower !== i18n.language) {
            i18n.changeLanguage(lower);
        }
    }, [urlLanguage, i18n]);

    const onSubmit = (event: any) => {
        setLoading(true);
        event.preventDefault();
        const password = event.target.password.value;
        const confirm = event.target.passwordConfirm.value;

        if (password !== confirm) {
            alert(t('passwordMismatch'));
            setLoading(false);
            return;
        }

        if (!email || !token) {
            alert(t('authError.invalidLink'));
            setLoading(false);
            return;
        }

        reset(email, token, password)
            .then((data: any) => {
                if (data && data.success) {
                    setLoading(false);
                    setStage('success');
                } else {
                    // Legacy path: backend returns 200 with { error } in body.
                    setLoading(false);
                    alert(translateAuthError(t, data, 'authError.resetFailed'));
                }
            })
            .catch((error) => {
                // Post-status-code-fix path: 401 (bad token) / 410 (expired
                // link) reject here — worth surfacing to the user so they know
                // to request a fresh reset email instead of retrying blindly.
                setLoading(false);
                alert(translateAuthError(t, error, 'authError.resetFailed'));
            });
    };

    const redirectToLogin = () => {
        navigate('/login');
    };

    if (loading) {
        return (
            <div className='flex h-screen w-screen items-center justify-center'>
                <CircularProgress />
            </div>
        );
    }

    return (
        <div className='container-auth relative'>
            <AuthForm
                className={`${stage === 'entry' ? 'flex' : 'hidden'}`}
                logoSrc={logo}
                onLogoClick={redirectToLogin}
            >
                <p className='mt-20 mb-4 text-xl font-bold text-[#757575] md:text-gray-700'>{t('reset')}</p>
                <form onSubmit={onSubmit} className='flex w-[360px] flex-col items-center md:w-full'>
                    <AuthFormLabel htmlFor='password'>{t('newPassword')}</AuthFormLabel>
                    <AuthFormInput id='password' name='password' type='password' autoComplete='new-password' />
                    <AuthFormLabel htmlFor='passwordConfirm'>{t('confirmPassword')}</AuthFormLabel>
                    <AuthFormInput
                        id='passwordConfirm'
                        name='passwordConfirm'
                        type='password'
                        className='mb-2'
                        autoComplete='new-password'
                    />
                    <p className='mt-[-15px] w-full px-[5px] text-xs text-gray-400 md:mt-0 md:w-[20rem] md:px-0'>
                        {t('passwordCheck')}
                    </p>
                    <AuthFormButton className='mt-2 md:w-[10rem]'>{t('reset')}</AuthFormButton>
                </form>
                <div className='mt-[30px] w-full text-center md:absolute md:bottom-2 md:left-1/2 md:mt-0 md:-translate-x-1/2'>
                    <p className='text-sm'>
                        {t('question')}{' '}
                        <a href='mailto:support@gocochain.com' className='text-blue-600 hover:underline'>
                            support@gocochain.com
                        </a>
                    </p>
                </div>
                <LanguageControl className='mt-6 md:hidden' />
            </AuthForm>

            <AuthForm
                className={`${stage === 'success' ? 'flex' : 'hidden'}`}
                logoSrc={logo}
                onLogoClick={redirectToLogin}
                topRight={<LanguageControl className='hidden md:block' />}
            >
                <p className='mt-32 mb-4 text-xl font-bold text-gray-700'>{t('success')}</p>
                <p className='mb-2 text-base text-gray-700'>{t('resetSuccess')}</p>
                <Link
                    to='/login'
                    className='flex h-10 w-full items-center justify-center rounded-full bg-blue-600 font-semibold text-white transition hover:bg-blue-700 md:w-[10rem]'
                >
                    {t('return')}
                </Link>
                <div className='md:absolute md:bottom-2 md:left-1/2 md:-translate-x-1/2 text-center'>
                    <p className='text-sm'>
                        {t('question')}{' '}
                        <a href='mailto:support@gocochain.com' className='text-blue-600 hover:underline'>
                            support@gocochain.com
                        </a>
                    </p>
                </div>
                <LanguageControl className='mt-6 md:hidden' />
            </AuthForm>
            <p className='absolute bottom-2 left-0 right-0 w-full px-2 text-center text-xs text-brandGray font-roboto'>
                © 2024 Gocochain Co., Ltd. All rights reserved.
            </p>
        </div>
    );
}

export default Reset;
